import React from "react";
import { Alert, Box, Typography, Stack, Button } from "@mui/material";
import SyncIcon from "@mui/icons-material/Sync";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import AppModal from "../common/AppModal";
import AppLinearProgress from "../common/AppLinearProgress";
import { ItemSyncReportDto } from "../../shared/dtos/item/ItemSyncReportDto";

interface SyncItemsModalProps {
  open: boolean;
  onClose: () => void;
  onSync: () => void;
  loading: boolean;
  error: string | null;
  report: ItemSyncReportDto | null;
}

const SyncItemsModal: React.FC<SyncItemsModalProps> = ({
  open,
  onClose,
  onSync,
  loading,
  error,
  report,
}) => {
  return (
    <AppModal
      open={open}
      onClose={loading ? () => {} : onClose} // Prevent closing during active sync
      title="Synchronize Branch POS"
      width="sm"
    >
      <Box sx={{ py: 1 }}>
        {/* SUCCESS STATE */}
        {report ? (
          <Stack spacing={2} sx={{ alignItems: "center", py: 2 }}>
            <CheckCircleOutlineIcon color="success" sx={{ fontSize: 60 }} />
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              Sync Completed Successfully
            </Typography>
            <Alert severity="success" sx={{ width: "100%" }}>
              {report.itemsSynced} items have been updated in your local branch
              POS system.
            </Alert>
            <Button
              fullWidth
              variant="contained"
              color="success"
              onClick={onClose}
              sx={{ mt: 2 }}
            >
              Done
            </Button>
          </Stack>
        ) : (
          /* IDLE OR LOADING STATE */
          <Stack spacing={3}>
            <Typography variant="body1" sx={{ color: "text.secondary" }}>
              This process will pull the latest products, prices, and tax
              configurations from the main catalog and update this branch&apos;s
              local records.
            </Typography>

            {error && <Alert severity="error">{error}</Alert>}

            {loading ? (
              <Stack spacing={2} sx={{ alignItems: "center", py: 3 }}>
                <AppLinearProgress label="Synchronizing databases... Please do not close your window." />
              </Stack>
            ) : (
              <Box>
                <Alert severity="info" sx={{ mb: 3 }}>
                  Ensure you have a stable internet connection before starting.
                </Alert>
                <Button
                  color="secondary"
                  variant="outlined"
                  fullWidth
                  startIcon={<SyncIcon />}
                  onClick={onSync}
                  disabled={loading}
                  sx={{ py: 1.5 }}
                >
                  Start Sync Now
                </Button>
              </Box>
            )}
          </Stack>
        )}
      </Box>
    </AppModal>
  );
};

export default SyncItemsModal;
